import { icon } from '@fortawesome/fontawesome-svg-core/import.macro';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useQueryClient } from 'react-query';
import { Link } from 'react-router-dom';
import { Box } from 'src/components/Box';
import { Button } from 'src/components/Button';
import { Dialog } from 'src/components/Dialog';
import { Icon } from 'src/components/Icon';
import { Typography } from 'src/components/Typography';
import { useOptionalSession } from 'src/contexts/SessionContext';
import { FeatureFlag } from 'src/models/platform/_types';
import {
  fetchOnboarding,
  fetchOnboardingKey,
  updateOnboarding,
} from 'src/models/platform-onboarding/platform-onboarding';
import { isTrainer } from 'src/models/user/utils';
import { hasFeatureFlag } from 'src/shared/usePermissionCheck';

export const WelcomeModal: React.FC = () => {
  const [open, setOpen] = useState(false);
  const { user, session } = useOptionalSession();
  const [loading, setLoading] = useState(false);
  const queryClient = useQueryClient();
  const { t } = useTranslation('translation', { keyPrefix: 'welcomeModal' });

  useQuery({
    queryKey: fetchOnboardingKey(session?.id || ''),
    queryFn: async () => {
      if (!session?.id) return null;
      return await fetchOnboarding(session.id);
    },
    enabled: !!session?.id && !!user && isTrainer(user),
    onSuccess: (data) => {
      if (data && !data.welcomed) {
        setOpen(true);
      }
    },
  });

  const onGoToOnboarding = async () => {
    if (!session?.id) {
      setOpen(false);
      return;
    }

    setLoading(true);
    try {
      await updateOnboarding(session.id, { welcomed: true });
      await queryClient.invalidateQueries(fetchOnboardingKey(session.id));
      setOpen(false);
    } catch (error) {
      // Error updating welcomed status
    } finally {
      setLoading(false);
    }
  };

  // If no session or onboarding feature flag not set, don't render
  if (!session?.id || !hasFeatureFlag(session, FeatureFlag.Onboarding)) {
    return null;
  }

  return (
    <Dialog
      open={open}
      title={t('title')}
      leftAdornment={<Icon icon={icon({ name: 'rocket', style: 'solid' })} />}
    >
      <Typography variant="body1" paragraph>
        {t('thankYou')}
      </Typography>
      <Typography variant="body1" paragraph>
        {t('getStarted')}
      </Typography>
      <Box sx={{ pl: 2, mb: 2 }}>
        <Typography variant="body1" component="div">
          • {t('setupItems.basicInfo')}
        </Typography>
        <Typography variant="body1" component="div">
          • {t('setupItems.brandConfigs')}
        </Typography>
        <Typography variant="body1" component="div">
          • {t('setupItems.technicalRequirements')}
        </Typography>
      </Box>
      <Typography variant="body1" paragraph>
        {t('accessNote')}
      </Typography>
      <Box sx={{ display: 'flex', justifyContent: 'center', mt: 3 }}>
        <Button
          component={Link}
          to="/onboarding"
          variant="contained"
          color="primary"
          onClick={onGoToOnboarding}
          size="medium"
          sx={{ px: 4 }}
          disabled={loading}
        >
          {loading ? t('buttons.loading') : t('buttons.goToOnboarding')}
        </Button>
      </Box>
    </Dialog>
  );
};
